//@ts-check

export default function (videojs, flvjs) {
    const Html5 = videojs.getTech('Html5');
    const mergeOptions = videojs.mergeOptions || videojs.util.mergeOptions;
    const defaults = {
        mediaDataSource: {},
        config: {}
    };

    class Flvjs extends Html5 {

        /**
         * Create an instance of this Tech.
         *
         * @param {Object} [options]
         *        The key/value store of player options.
         *
         * @param {function} ready
         *        Callback function to call when the `Flvjs` Tech is ready.
         */
        constructor(options, ready) {
            options = mergeOptions(defaults, options);
            super(options, ready);

            // We want to wait for playback first to start loading
            if (!options.mediaDataSource.autoLoad) {
                const onFirstPlay = () => {
                    this.el_.removeEventListener('play', onFirstPlay);
                    this.flvPlayer.load();
                };

                this.el_.addEventListener('play', onFirstPlay);
            }
        }

        /**
         * A getter/setter for the `Flvjs` Tech's source object.
         *
         * @param {object} [src]
         *        The source object you want to set on the `Flvjs` techs.
         *
         * @return {object|undefined}
         *         - The current source object when a source is not passed in.
         *         - undefined when setting
         */
        setSrc(src) {
            if (this.flvPlayer) {
                // Is this necessary to change source?
                this.flvPlayer.detachMediaElement();
                this.flvPlayer.destroy();
            }

            // Get config options
            const { mediaDataSource, config } = this.options_;

            // Add media type and url
            mediaDataSource.type = mediaDataSource.type === undefined ? 'flv' : mediaDataSource.type;
            mediaDataSource.url = src;

            // Pass options to flv.js and setup it on the <video> element
            this.flvPlayer = flvjs.createPlayer(mediaDataSource, config);
            this.flvPlayer.attachMediaElement(this.el_);

            // Start loading immediatelly
            if (mediaDataSource.autoLoad) {
                this.flvPlayer.load();
            }

            // Pass the error to Videojs
            this.flvPlayer.on('error', (e1, e2, e3) => {
                this._error = { message: e3.msg };
                this.trigger('error');
            });

            // Got statistics info
            this.flvPlayer.on('statistics_info', (e) => {
                const params = [];
                for (const param of ['speed', 'decodedFrames', 'droppedFrames']) {
                    params.push(`${param}=${e[param]}`);
                }

                console.log(params.join(', '));

                this.trigger('stats', e);
            });
        }

        /**
         * Get last error
         */
        error() {
            if (!this._error) return null;

            return { code: 2, message: this._error.message };
        }

        /**
         * Dispose of flvjs.
         */
        dispose() {
            if (this.flvPlayer) {
                this.flvPlayer.detachMediaElement();
                this.flvPlayer.destroy();
            }
            super.dispose();
        }

        /**
         * Check if the Flvjs tech is currently supported.
         *
         * @return {boolean}
         *          - True if the Flvjs tech is supported.
         *          - False otherwise.
         */
        static isSupported() {
            return flvjs.isSupported();
        };

        /**
         * Flvjs supported mime types.
         *
         * @constant {Object}
         */
        static get formats() {
            return {
                'video/flv': 'FLV',
                'video/x-flv': 'FLV'
            };
        };

        /**
         * Check if the tech can support the given type
         *
         * @param {string} type
         *        The mimetype to check
         * @return {string} 'probably', 'maybe', or '' (empty string)
         */
        static canPlayType(type) {
            if (this.isSupported() && type in this.formats) {
                return 'maybe';
            }

            return '';
        };

        /**
         * Check if the tech can support the given source
         * @param {Object} srcObj
         *        The source object
         * @param {Object} options
         *        The options passed to the tech
         * @return {string} 'probably', 'maybe', or '' (empty string)
         */
        static canPlaySource(srcObj, options) {
            return this.canPlayType(srcObj.type);
        };

        static get VERSION() {
            return flvjs.version;
        }
    }

    videojs.registerTech('Flvjs', Flvjs);
};